import { useState, KeyboardEvent, useRef } from 'react';
import { Tag } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { MultiEmailInputStyle } from './MultiEmailInput.style';

interface MultiEmailInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MultiEmailInput = ({ value, onChange, placeholder = 'Enter email addresses', disabled = false }: MultiEmailInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const addEmail = (emailToAdd: string) => {
    const trimmedEmail = emailToAdd.trim();
    
    if (!trimmedEmail) return;
    
    if (!validateEmail(trimmedEmail)) {
      // Could add error toast here if needed
      return;
    }

    // Check for duplicates (case-insensitive)
    const isDuplicate = value.some(
      (existingEmail) => existingEmail.toLowerCase() === trimmedEmail.toLowerCase()
    );

    if (isDuplicate) {
      return;
    }

    onChange([...value, trimmedEmail]);
    setInputValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === ' ') {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last email when backspace is pressed on empty input
      const newEmails = [...value];
      newEmails.pop();
      onChange(newEmails);
    }
  };

  const handleBlur = () => {
    setIsInputFocused(false);
    // Add email on blur if there's content
    if (inputValue.trim()) {
      addEmail(inputValue);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    // Split by common separators: comma, semicolon, space, newline
    const emails = pastedText
      .split(/[,;\s\n]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    const validEmails = emails.filter(email => validateEmail(email));
    const uniqueEmails = validEmails.filter(
      email => !value.some(
        existing => existing.toLowerCase() === email.toLowerCase()
      )
    );

    if (uniqueEmails.length > 0) {
      onChange([...value, ...uniqueEmails]);
    }
    
    setInputValue('');
  };

  const removeEmail = (indexToRemove: number) => {
    onChange(value.filter((_, index) => index !== indexToRemove));
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  return (
    <MultiEmailInputStyle
      className={`multi-email-input ${isInputFocused ? 'focused' : ''} ${disabled ? 'disabled' : ''}`}
      onClick={handleContainerClick}
    >
      <div className="email-tags-container">
        {value.map((email, index) => (
          <Tag
            key={index}
            closable={!disabled}
            onClose={() => removeEmail(index)}
            closeIcon={<CloseOutlined />}
            className="email-tag"
          >
            {email}
          </Tag>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsInputFocused(true)}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          className="email-input"
        />
      </div>
    </MultiEmailInputStyle>
  );
};

export default MultiEmailInput;

